import { clientbookApi } from '../core/clientbook-api';

/**
 * Get list of mass messages (filtered by query params like eventId)
 * @param {Object} options
 * @param {Object} options.query - Query params (eventId, page, limit, etc.)
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const getMassMessages = async ({ query = {}, loadingStatusOff = false }) => {
  return clientbookApi.get({
    url: '/merchants/mass-messages',
    query,
    loading: !loadingStatusOff,
  });
};

/**
 * Get single mass message by ID
 * @param {Object} options
 * @param {string|number} options.massMessageId - Mass message ID
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const getMassMessage = async ({ massMessageId, loadingStatusOff = false }) => {
  return clientbookApi.get({
    url: `/merchants/mass-messages/${massMessageId}`,
    loading: !loadingStatusOff,
  });
};

/**
 * Create a new mass message
 * @param {Object} options
 * @param {Object} options.data - Mass message data
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const createMassMessage = async ({ data, loadingStatusOff = false }) => {
  return clientbookApi.post({
    url: '/merchants/mass-messages',
    data,
    loading: !loadingStatusOff,
  });
};

/**
 * Update an existing mass message
 * @param {Object} options
 * @param {string|number} options.massMessageId - Mass message ID
 * @param {Object} options.data - Updated mass message data
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const updateMassMessage = async ({ massMessageId, data, loadingStatusOff = false }) => {
  return clientbookApi.patch({
    url: `/merchants/mass-messages/${massMessageId}`,
    data,
    loading: !loadingStatusOff,
  });
};

/**
 * Archive (delete) a mass message
 * @param {Object} options
 * @param {string|number} options.massMessageId - Mass message ID
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const archiveMassMessage = async ({ massMessageId, loadingStatusOff = false }) => {
  return clientbookApi.delete({
    url: `/merchants/mass-messages/${massMessageId}`,
    loading: !loadingStatusOff,
  });
};

/**
 * Cancel a scheduled mass message
 * @param {Object} options
 * @param {string|number} options.massMessageId - Mass message ID
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const cancelMassMessage = async ({ massMessageId, loadingStatusOff = false }) => {
  return clientbookApi.delete({
    url: `/merchants/mass-messages/${massMessageId}`,
    loading: !loadingStatusOff,
  });
};

/**
 * Add attachments to a mass message
 * @param {Object} options
 * @param {string|number} options.massMessageId - Mass message ID
 * @param {string[]} options.fileUrls - Array of S3 file URLs
 */
export const addMassMessageAttachments = async ({ massMessageId, fileUrls }) => {
  return clientbookApi.post({
    url: `/merchants/mass-messages/${massMessageId}/attachments`,
    data: { fileUrls },
    loading: false,
  });
};

/**
 * Get attachments for a mass message
 * @param {Object} options
 * @param {string|number} options.massMessageId - Mass message ID
 */
export const getMassMessageAttachments = async ({ massMessageId }) => {
  return clientbookApi.get({
    url: `/merchants/mass-messages/${massMessageId}/attachments`,
    loading: false,
  });
};

/**
 * Remove an attachment from a mass message
 * @param {Object} options
 * @param {string|number} options.massMessageId - Mass message ID
 * @param {string|number} options.attachmentId - Attachment ID
 */
export const removeMassMessageAttachment = async ({ massMessageId, attachmentId }) => {
  return clientbookApi.delete({
    url: `/merchants/mass-messages/${massMessageId}/attachments/${attachmentId}`,
    loading: false,
  });
};

/**
 * Get aggregate metrics for mass messages
 * @param {Object} options
 * @param {Object} options.query - Query params (status, scheduledAfter, scheduledBefore)
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const getMassMessageMetrics = async ({ query = {}, loadingStatusOff = false }) => {
  return clientbookApi.get({
    url: '/merchants/mass-messages/metrics',
    query,
    loading: !loadingStatusOff,
  });
};

/**
 * Get merchant plan details
 */
export const getMerchantPlan = async () => {
  return clientbookApi.get({
    url: '/merchants/plan',
    loading: false,
  });
};
